<template>
    <div class="w-full">
        <h4 class="font-bold text-gray-900 mb-1" v-html="languageSupportTitle"></h4>
        <p class="text-sm text-gray-600 mb-4" v-html="languageSupportSubtitle"></p>
        <div class="relative w-full max-w-xs" ref="dropdownRef">
            <button
                type="button"
                class="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-brand-blue hover:border-brand-blue transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/20"
                :class="{ 'border-brand-blue': isOpen }"
                @click="isOpen = !isOpen"
                aria-haspopup="listbox"
                :aria-expanded="isOpen"
            >
                <span class="flex items-center gap-2">
                    <SvgIcon name="language" class="text-xl" />
                    <span v-html="selectLabel"></span>
                </span>
                <SvgIcon name="expand_more" class="text-lg transition-transform" :class="{ 'rotate-180': isOpen }" />
            </button>
            <div
                v-show="isOpen"
                class="absolute left-0 mt-2 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-2xl z-20 divide-y divide-gray-50"
            >
                <button
                    v-for="lang in languages"
                    :key="lang.code"
                    type="button"
                    class="w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-center gap-3 transition-colors"
                    :class="{ 'bg-blue-50': isCurrentLanguage(lang.code) }"
                    @click="selectLanguage(lang)"
                >
                    <img
                        v-if="lang.flag"
                        :src="lang.flag"
                        :alt="langName(lang)"
                        class="w-6 h-5 object-contain rounded-sm"
                    />
                    <span v-else class="text-lg">{{ flagEmoji(lang.code) }}</span>
                    <span class="text-sm font-medium text-gray-700">{{ langName(lang) }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';

defineOptions({ name: 'LanguageSelectorBlock' });

/**
 * Language item from Vuex (store.getters.languages).
 * Full namespace for display name: language.translated.name
 * @typedef {Object} LanguageItem
 * @property {number} id
 * @property {boolean} default
 * @property {boolean} right_to_left
 * @property {number} index
 * @property {string} code
 * @property {string} flag
 * @property {string} ref_code
 * @property {string} code_view
 * @property {{ name: string }} translated - full path for label: language.translated.name
 */

/** Full path for language display label (API: language.translated.name) */
const LANG_DISPLAY_NAME_PATH = 'translated.name';

const store = useStore();
const isOpen = ref(false);
const dropdownRef = ref(null);

const languages = computed(() => store.getters.languages || []);
const currentLang = computed(() => store.getters.lang || null);
const texts = computed(() => store.getters.texts || {});

/** Same keys as Blade `$__field['contents'][…]` for section `home-section-5`. */
const homeSection5Contents = computed(() => {
    const fields = store.getters.page?.fields;
    if (!Array.isArray(fields)) return {};
    const field = fields.find((f) => f?.section === 'home-section-5');
    const c = field?.contents;
    return c && typeof c === 'object' ? c : {};
});

function pickHomeSection5Text(key, fallback) {
    const fromTexts = texts.value[key];
    if (fromTexts != null && String(fromTexts).trim() !== '') return fromTexts;
    const raw = homeSection5Contents.value[key];
    if (raw != null && String(raw).trim() !== '') return raw;
    return fallback;
}

const languageSupportTitle = computed(() => {
    const n = languages.value.length;
    const key = 'home-section-5.blade.php-5-block-2-item-1-title';
    const fromField = pickHomeSection5Text(key, '');
    if (fromField) return fromField;
    return n ? `${n}-language support` : '22-language support';
});

const languageSupportSubtitle = computed(() =>
    pickHomeSection5Text(
        'home-section-5.blade.php-5-block-2-item-1-text',
        'Native support for international drivers across the continent.'
    )
);

const selectLabel = computed(() =>
    pickHomeSection5Text('home-section-5.blade.php-5-block-2-select-language', 'Select your language')
);

function langName(lang) {
    return lang?.translated?.name || lang?.code_view || lang?.code || '';
}

function flagEmoji(code) {
    if (!code || code.length !== 2) return '🌐';
    const a = 0x1F1E6 - 65;
    const b = 0x1F1E6 - 65;
    const c1 = code.charCodeAt(0);
    const c2 = code.charCodeAt(1);
    const u1 = 0x1F1E6 + (c1 >= 97 ? c1 - 97 : c1 - 65);
    const u2 = 0x1F1E6 + (c2 >= 97 ? c2 - 97 : c2 - 65);
    return String.fromCodePoint(u1, u2);
}

function isCurrentLanguage(code) {
    const cur = currentLang.value?.code;
    if (!cur || !code) return false;
    return String(code).toLowerCase() === String(cur).toLowerCase();
}

function selectLanguage(lang) {
    isOpen.value = false;
    const langCode = lang?.code;
    if (!langCode) return;
    const page = store.state.page;
    const path = page?.paths_by_language?.[langCode];
    if (path) {
        window.location.replace(path);
    }
}

function handleClickOutside(e) {
    if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
        isOpen.value = false;
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.rotate-180 {
    transform: rotate(180deg);
}
</style>
